import React, { Fragment, ReactNode } from 'react';
import { Field } from 'react-final-form';
import { useIntl } from 'react-intl';

import { Grid, Notification } from '@xsolla/ui-core';

import { LinkStylesUnderline, Notice } from '../../../ui';
import { Scope } from '../scopeValues/types';
import { MainContentBox, ThinContentBox } from '../style';
import {
  CheckBoxPill,
  DataWrapper,
  NotificationWrapper,
  StyledCheckbox,
  UserDataFormTitle,
  UserDataSubtitle,
  UserDataTitle
} from './style';

type Props = {
  name: string;
  provider: string;
  disabled?: boolean;
  permissionDocLink?: string;
  scopes: Scope[];
  title: ReactNode;
  subtitle: ReactNode;
};

export const CheckBoxBlock = ({
  scopes,
  provider,
  title,
  subtitle,
  permissionDocLink,
  name,
  disabled = false
}: Props): JSX.Element => {
  const intl = useIntl();

  if (scopes.length === 0) {
    return <></>;
  }

  return (
    <MainContentBox>
      <ThinContentBox>
        {disabled && (
          <NotificationWrapper>
            <Notification icon={Notice}>
              {intl.formatMessage({
                id: 'social-connection.settings.scopes-disabled.notification'
              })}
            </Notification>
          </NotificationWrapper>
        )}

        <DataWrapper>
          <UserDataTitle>{title}</UserDataTitle>
          <UserDataSubtitle>{subtitle}</UserDataSubtitle>

          {permissionDocLink && (
            <Notification icon={Notice}>
              {intl.formatMessage(
                { id: 'social-connection.settings.permission.notification' },
                {
                  name: intl.formatMessage({
                    id: `auth-methods.${provider}`
                  }),
                  a: (chunk: string) => (
                    <LinkStylesUnderline>
                      <a
                        href={permissionDocLink}
                        data-test-id="scopes_link"
                        target="_blank"
                        rel="noreferrer">
                        {chunk}
                      </a>
                    </LinkStylesUnderline>
                  )
                }
              )}
            </Notification>
          )}
        </DataWrapper>

        <UserDataFormTitle>{title}</UserDataFormTitle>

        <Grid cols={2} gap={14}>
          {scopes.map((item) => {
            return (
              <Fragment key={item.value}>
                <Field name={`scopesValues.${item.name}`} type="checkbox">
                  {({ input }) => (
                    <StyledCheckbox
                      {...input}
                      key={item.value}
                      tooltip={
                        intl.messages[
                          `social-connection.settings.${name}.${provider}.${item.name}.help`
                        ]
                          ? intl.formatMessage({
                              id: `social-connection.settings.${name}.${provider}.${item.name}.help`
                            })
                          : ''
                      }
                      disabled={disabled || item.required}
                      isRequired={item.required}
                      {...(item.required && { checked: true })}>
                      {intl.messages[`social-connection.settings.${name}.${item.name}`]}
                    </StyledCheckbox>
                  )}
                </Field>
                <CheckBoxPill>{item.value}</CheckBoxPill>
              </Fragment>
            );
          })}
        </Grid>
      </ThinContentBox>
    </MainContentBox>
  );
};
